import {
  APP_NAME,
  RequestMethod,
  TEST_ENV_QUERY_PARAM,
} from '~/shared/constants/commonConstants'
import { ApiService } from '~/shared/services/api'
import { IRequestOptions, IResponseResult } from '~/shared/types/apiTypes'
import { TAnswers } from '~/shared/types/commonTypes'
import {
  IAutologinTokenResponseRaw,
  IGetUserStatusResponseRaw,
  IPredictedLtvResponseRaw,
  IUserRawAccountStatus,
  TUserConfig,
} from '~/shared/types/userTypes'

export class UserApi {
  private readonly baseUrlV2 = 'v2/leads'
  private api: ApiService

  constructor(api: ApiService) {
    this.api = api
  }

  getUUID({
    cohort,
    appId,
    locale,
    promoCode = '',
    utm,
  }: {
    cohort: string
    appId: string
    locale: string
    promoCode?: string
    utm: Record<string, string>
  }): Promise<IResponseResult<string>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        cohort,
        locale,
        app_id: appId,
        app_name: APP_NAME,
        utm,
        promo_code: promoCode,
        is_test_environment:
          document.location.search.includes(TEST_ENV_QUERY_PARAM),
      },
    }

    return this.api.makeRequest<string>(this.baseUrlV2, options)
  }

  saveFacebookParams({
    fbc,
    fbp,
    uuid,
  }: {
    uuid: string
    fbc: string
    fbp: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        fbc,
        fbp,
      },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/${APP_NAME}/facebook`,
      options,
    )
  }

  saveGoogleAdsParams({
    uuid,
    gclid,
    gaid,
  }: {
    uuid: string
    gclid: string
    gaid: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      params: { ctid: gclid, gaid },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/google`,
      options,
    )
  }

  saveUserTTCLID({
    ttclid,
    uuid,
  }: {
    ttclid: string
    uuid: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      params: { ttclid },
    }

    return this.api.makeRequest(`${this.baseUrlV2}/${uuid}/tiktok`, options)
  }

  saveUserAnswers({
    uuid,
    answers,
    isFinished = false,
    isSkipped = false,
  }: {
    uuid: string
    answers: TAnswers
    isFinished?: boolean
    isSkipped?: boolean
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        is_finished: isFinished,
        is_skipped: isSkipped,
        quiz: answers,
        version: '2.0',
      },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/${APP_NAME}/quiz`,
      options,
    )
  }

  saveUserEmail({
    uuid,
    email,
  }: {
    uuid: string
    email: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        email,
      },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/${APP_NAME}/email`,
      options,
    )
  }

  saveUserConfig({
    uuid,
    config,
  }: {
    uuid: string
    config: TUserConfig
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        config,
      },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/${APP_NAME}/config`,
      options,
    )
  }

  getUserStatus(
    uuid: string,
  ): Promise<IResponseResult<IGetUserStatusResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.GET,
    }

    return this.api.makeRequest<IGetUserStatusResponseRaw>(
      `${this.baseUrlV2}/${uuid}/${APP_NAME}`,
      options,
    )
  }

  getPredictedLtv(
    uuid: string,
  ): Promise<IResponseResult<IPredictedLtvResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.GET,
    }

    return this.api.makeRequest<IPredictedLtvResponseRaw>(
      `${this.baseUrlV2}/${uuid}/${APP_NAME}/predicted_ltv`,
      options,
    )
  }

  savePlanAdditions({
    uuid,
    planAdditions,
  }: {
    uuid: string
    planAdditions: string[]
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: { addons: planAdditions },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/${APP_NAME}/addons`,
      options,
    )
  }

  getAccountStatus(
    email: string,
  ): Promise<IResponseResult<IUserRawAccountStatus>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        app_name: APP_NAME,
        email,
      },
    }

    return this.api.makeRequest<IUserRawAccountStatus>(
      `${this.baseUrlV2}/account/status`,
      options,
    )
  }

  bindUser({
    uuid,
    token,
  }: {
    uuid: string
    token: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: { token },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/${APP_NAME}/signup`,
      options,
    )
  }

  saveEmailConsent({
    uuid,
    consented,
  }: {
    uuid: string
    consented: boolean
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        consented,
      },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/${APP_NAME}/email/consent`,
      options,
    )
  }

  getAutologinToken(
    uuid: string,
  ): Promise<IResponseResult<IAutologinTokenResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        uuid,
        app_name: APP_NAME,
      },
    }

    return this.api.makeRequest<IAutologinTokenResponseRaw>(
      'v1/flow/login',
      options,
    )
  }

  sendRetenoEvent({
    uuid,
    retenoEvent,
  }: {
    uuid: string
    retenoEvent: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        app_name: APP_NAME,
        type: retenoEvent,
        uuid,
      },
    }

    return this.api.makeRequest<IGetUserStatusResponseRaw>(
      'v2/reteno/event',
      options,
    )
  }
}
